<template>
  <div>
    <div class="page-top">
      <span class="back" @click="goBack">返回</span>
    </div>

    <el-empty v-if="!videoId" description="请从课程目录选择视频"></el-empty>

    <template v-else>
      <div class="player-box">
        <video
          ref="video"
          class="video"
          :src="videoUrl"
          preload="auto"
          playsinline
          @loadedmetadata="onLoadedMeta"
          @timeupdate="onTimeUpdate"
          @play="onPlay"
          @pause="onPause"
          @ended="onEnded"
          @error="onError"
        ></video>

        <div v-if="!videoUrl" class="mask">
          <i class="el-icon-loading"></i>
          <span>视频加载中...</span>
        </div>

        <div class="controls">
          <span class="ctrl-btn" @click="togglePlay">
            <i :class="playing ? 'el-icon-video-pause' : 'el-icon-video-play'"></i>
          </span>
          <span class="time">{{ formatTime(currentTime) }}</span>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <span class="time">{{ formatTime(duration) }}</span>
        </div>
      </div>

      <div class="tips">
        当前进度：{{ formatTime(currentTime) }}
      </div>
    </template>
  </div>
</template>

<script>
import { getVideoDetail, getVideoProgress, saveVideoProgress } from '../api'
import { resolveMediaUrl, formatTime } from '../utils/media'

export default {
  name: 'VideoStudy',
  data() {
    return {
      listId: '',
      videoId: '',
      videoUrl: '',
      playing: false,
      currentTime: 0,
      duration: 0,
      resumeTime: 0,
      lastReported: 0,
      reportTimer: null
    }
  },
  computed: {
    progressPercent() {
      if (!this.duration) return 0
      return Math.min(100, (this.currentTime / this.duration) * 100)
    }
  },
  created() {
    this.listId = this.$route.query.listId || ''
    this.videoId = this.$route.query.videoId || ''
    if (this.videoId) {
      this.init()
    }
  },
  beforeDestroy() {
    this.stopReport()
    this.reportOnce()
  },
  methods: {
    formatTime,
    async init() {
      try {
        const detail = await getVideoDetail(this.videoId)
        this.videoUrl = resolveMediaUrl(detail.kj_video)
        const t = parseFloat(detail.kj_time)
        if (!isNaN(t) && t > 0) {
          this.duration = t
        }
      } catch (e) {
        this.$message.error('获取视频信息失败')
      }
      this.fetchProgress()
    },
    async fetchProgress() {
      try {
        const res = await getVideoProgress(this.listId, this.videoId)
        const progress = res && res.data && res.data.progress
        if (progress != null) {
          this.applyResume(Number(progress) || 0)
        }
      } catch (e) {
        // 进度获取失败时从 0 开始
      }
    },
    applyResume(t) {
      const video = this.$refs.video
      if (video && video.readyState >= 1 && t > 0) {
        if (!video.duration || t < video.duration) {
          video.currentTime = t
          this.currentTime = t
          this.lastReported = t
          return
        }
      }
      this.resumeTime = t
    },
    onLoadedMeta() {
      const video = this.$refs.video
      if (video.duration) {
        this.duration = video.duration
      }
      if (this.resumeTime > 0) {
        if (this.resumeTime < video.duration - 1) {
          video.currentTime = this.resumeTime
          this.currentTime = this.resumeTime
          this.lastReported = this.resumeTime
        }
        this.resumeTime = 0
      }
    },
    onTimeUpdate() {
      this.currentTime = this.$refs.video.currentTime
    },
    onPlay() {
      this.playing = true
      this.startReport()
    },
    onPause() {
      this.playing = false
    },
    onEnded() {
      this.playing = false
      const video = this.$refs.video
      if (video && video.duration) {
        this.saveProgress(video.duration)
      }
      this.$message.success('视频学习完成')
    },
    onError() {
      this.$message.error('视频加载失败，请稍后重试')
    },
    togglePlay() {
      const video = this.$refs.video
      if (!video) return
      if (video.paused) {
        video.play().catch(() => {})
      } else {
        video.pause()
      }
    },
    startReport() {
      if (this.reportTimer) return
      this.reportTimer = setInterval(() => {
        const video = this.$refs.video
        if (!video) return
        const t = video.currentTime || 0
        if (Math.abs(t - this.lastReported) >= 1) {
          this.saveProgress(t)
        }
      }, 10000)
    },
    stopReport() {
      if (this.reportTimer) {
        clearInterval(this.reportTimer)
        this.reportTimer = null
      }
    },
    async saveProgress(seconds) {
      this.lastReported = seconds
      try {
        await saveVideoProgress(this.listId, this.videoId, seconds)
      } catch (e) {
        // 上报失败不影响播放
      }
    },
    reportOnce() {
      const video = this.$refs.video
      if (
        video &&
        video.currentTime > 0 &&
        Math.abs(video.currentTime - this.lastReported) >= 1
      ) {
        this.saveProgress(video.currentTime)
      }
    },
    goBack() {
      this.$router.back()
    }
  }
}
</script>

<style scoped>
.page-top {
  max-width: 100%;
  margin: 0 auto 12px;
}

.player-box {
  position: relative;
  max-width:100%;
  margin: 0 auto;
  background: #000;
  border-radius: 6px;
  overflow: hidden;
}

.video {
  display: block;
  width: 100%;
  max-height: 60vh;
  background: #000;
}

.mask {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
}

.controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: #111;
  color: #fff;
}

.ctrl-btn {
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.time {
  font-size: 12px;
  color: #c0c4cc;
  font-variant-numeric: tabular-nums;
}

.progress-bar {
  flex: 1;
  height: 6px;
  background: #333;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #409eff;
  border-radius: 3px;
  transition: width 0.2s linear;
}

.tips {
  max-width: 720px;
  margin: 12px auto 0;
  font-size: 13px;
  color: #909399;
  text-align: center;
}
</style>
